import { ChangeDetectionStrategy, Component, Input, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { toSignal } from '@angular/core/rxjs-interop';
import { RoutesUrl } from '@app/routes.enum';
import { filter, switchMap, of, catchError } from 'rxjs';
import { Liga } from '@app/models/liga.model';

import { ServicioBarraSuperior } from '@app/services/general/barra-superior.service';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import { ThemingService } from '@app/services/general/theming.service';
import { CrmService } from '@app/services/crm/crm.service';

interface OpcionMenu {
  nombre: string;
  ruta?: string;
  icono: string;
  hijos?: OpcionMenu[];
  nietos?: OpcionMenu[];
  desplegado?: boolean;
  roles?: string[];
  permiso?: string | string[]; // Array = basta con tener cualquiera de esos permisos
  tipo?: ('GLOBAL' | 'LIGA' | 'EQUIPO')[]; // Si se omite, visible para todos
  preserveQueryParams?: boolean;
  requiereConfigCrm?: boolean; // Oculta la opción si el contexto activo no tiene CrmConfig
}

@Component({
  selector: 'app-barra-lateral',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, MatIconModule, MatTooltipModule],
  templateUrl: './barra-lateral.component.html',
  styleUrls: ['./barra-lateral.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarraLateralComponent {
  @Input() liga: Liga | null = null;
  public routesUrl = RoutesUrl;

  private _servicioBarra = inject(ServicioBarraSuperior);
  private _router = inject(Router);
  private _sesion = inject(ServicioSesion);
  private _theming = inject(ThemingService);
  private _crmService = inject(CrmService);

  public menuAbierto = this._servicioBarra.menuLateralAbierto;
  public rolActivoSignal = toSignal(this._sesion.rolActivo$);
  private usuarioSignal = toSignal(this._sesion.usuarioActual$);

  private _crmConfigDisponible = toSignal(
    this._sesion.rolActivo$.pipe(
      switchMap(rol => {
        if (!rol) return of(false);
        const filtro = rol.tipo === 'LIGA' ? { liga_id: rol.ligaId } : rol.tipo === 'EQUIPO' ? { escuela_id: rol.equipoId } : {};
        return this._crmService.tieneConfig(filtro).pipe(catchError(() => of(false)));
      }),
    ),
    { initialValue: false },
  );

  public menuColapsado = signal<boolean>(false);
  public modoOscuro = signal<boolean>(this._theming.esModoOscuroActivo());


  public menuCompleto: OpcionMenu[] = [
    {
      nombre: 'Dashboard',
      ruta: RoutesUrl.DASHBOARD,
      icono: 'tt_dashboard1',
      tipo: ['LIGA', 'EQUIPO'],
      preserveQueryParams: true
    },
    {
      nombre: 'Panel Root',
      ruta: RoutesUrl.DASHBOARD_ROOT,
      icono: 'tt_dashboard1',
      tipo: ['GLOBAL'],
      roles: ['ROOT', 'CRM ROOT'],
    },

    {
      nombre: 'Mi Equipo',
      ruta: RoutesUrl.MIEQUIPO,
      icono: 'tt_cronometro',
      tipo: [], // Oculto: módulo de equipos no se usa ya, sin borrar el código.
      roles: ['PROPIETARIO', 'ROOT_EQUIPO', 'ADMINISTRADOR'],
    },
    {
      nombre: 'Reportes',
      icono: 'tt_silbato',
      desplegado: true,
      tipo: ['LIGA'],
      roles: ['ADMIN'],
      //permiso: 'ver_finanzas'
      hijos: [
        {
          nombre: 'Marcadores',
          icono: '',
          nietos: [
            {
              nombre: 'Por ID',
              ruta: RoutesUrl.REPORTES_MARC_ID,
              icono: '',
              preserveQueryParams: true
            },
            {
              nombre: 'Por Sede',
              ruta: RoutesUrl.REPORTES_MARC_SEDE,
              icono: '',
              preserveQueryParams: true
            },
            {
              nombre: 'Por Categoría',
              ruta: RoutesUrl.REPORTES_MARC_CATEGORIA,
              icono: '',
              preserveQueryParams: true
            },
            /*{
              nombre: 'Por Fecha',
              ruta: RoutesUrl.REPORTES_MARC_FECHA,
              icono: ''
            },*/
            {
              nombre: 'Por Tipo',
              ruta: RoutesUrl.REPORTES_MARC_TIPO,
              icono: '',
              preserveQueryParams: true
            },
            {
              nombre: 'Por Equipo',
              ruta: RoutesUrl.REPORTES_MARC_EQUIPO,
              icono: '',
              preserveQueryParams: true
            },
            {
              nombre: 'Incidencias',
              ruta: RoutesUrl.REPORTES_MARC_INCIDENCIAS,
              icono: '',
              preserveQueryParams: true
            },
          ]
        },
        /*{
          nombre: 'Partidos x Equipo',
          ruta: RoutesUrl.REPORTES_EQ_PARTIDOS,
          icono: ''
        }*/
      ]
    },

    {
      nombre: 'Equipos',
      ruta: RoutesUrl.EQUIPOS,
      icono: 'tt_cronometro',
      tipo: [], // Oculto: módulo de equipos no se usa ya, sin borrar el código.
      roles: ['ADMIN'],
    },
    {
      nombre: 'Escuelas',
      ruta: RoutesUrl.ESCUELAS,
      icono: 'tt_school',
      tipo: ['GLOBAL'],
      roles: ['ROOT'],
    },
    {
      nombre: 'Ligas',
      ruta: RoutesUrl.LIGAS,
      icono: 'tt_configuracion',
      tipo: [],
      roles: ['ROOT', 'ADMIN'],
    },
    {
      nombre: 'Sedes',
      ruta: RoutesUrl.SEDES,
      icono: 'tt_estadio',
      tipo: ['LIGA'],
      permiso: 'gestionar-sedes-liga',
    },
    {
      nombre: 'Jugadores',
      ruta: RoutesUrl.JUGADORES,
      icono: 'tt_equipos',
      tipo: ['LIGA'],
      roles: ['ROOT', 'ADMIN'],
      //permiso: 'ver_finanzas'
    },
    {
      nombre: 'Noticias',
      ruta: RoutesUrl.NOTICIAS,
      icono: 'tt-americano-news',
      tipo: ['LIGA'],    
      roles: ['ROOT', 'ADMIN'],
    },
    {
      nombre: 'Familias',
      ruta: RoutesUrl.FAMILIAS,
      icono: 'tt_equipos',
      tipo: ['LIGA'],
      roles: ['ROOT'],
    },
    {
      nombre: 'Alumnos',
      ruta: RoutesUrl.ALUMNOS,
      icono: 'tt_students1',
      tipo: ['EQUIPO'],
      permiso: ['gestionar-alumnos', 'ver-alumnos'],
    },
    {
      nombre: 'Staff',
      ruta: RoutesUrl.MISTAFF,
      icono: 'tt-employee',
      tipo: ['EQUIPO'],
      permiso: 'gestionar-staff',
    },

    {
      nombre: 'Torneos',
      ruta: RoutesUrl.TORNEOS,
      icono: 'tt_trofeo',
      tipo: [], // Oculto: concepto deportivo sin equivalente escolar.
      roles: ['ROOT', 'ADMIN','PROPIETARIO','ADMINISTRADOR', 'ROOT_EQUIPO', 'COACH'],
    },

    {
      nombre: 'Directorio de equipos',
      ruta: RoutesUrl.EQUIPOS,
      icono: 'tt-americano-chat',
      tipo: [], // Oculto: módulo de equipos no se usa ya, sin borrar el código.
      roles: ['PROPIETARIO', 'ADMINISTRADOR', 'COACH', 'FINANCIERO', 'ROOT_EQUIPO'],
    },

    {
      nombre: 'CRM',
      ruta: RoutesUrl.CRM,
      icono: 'tt_crm',
      tipo: ['EQUIPO', 'GLOBAL'],
      permiso: 'crm-full',
      requiereConfigCrm: true,
    },

    {
      nombre: 'CRM Liga',
      ruta: RoutesUrl.CRMLI,
      icono: 'tt_crm',
      tipo: ['LIGA'],
      roles: ['ROOT', 'ADMIN'],
      requiereConfigCrm: true,
    },

    {
      nombre: 'Finanzas',
      ruta: '',
      icono: 'tt_invoice',
      tipo: ['EQUIPO'],
      permiso: ['gestionar-financiero', 'ver-financiero'],
      hijos: [
        {
          nombre: 'Ingresos y CPC',
          ruta: RoutesUrl.FIN_INGRESOS,
          icono: ''
        },
        {
          nombre: 'Gastos',
          ruta: RoutesUrl.FIN_EGRESOS,
          icono: ''
        },
        {
          nombre: 'Comisiones',
          ruta: RoutesUrl.FIN_COMISIONES,
          icono: ''
        },
        {
          nombre: 'Saldos de Alumnos',
          ruta: RoutesUrl.FIN_SALDOS,
          icono: ''
        },
        {
          nombre: 'Facturas',
          ruta: RoutesUrl.FIN_FACTURAS,
          icono: ''
        },
        {
          nombre: 'Configuración',
          icono: '',
          nietos:[
            {
              nombre: 'Descuentos',
              ruta: RoutesUrl.FIN_DESCUENTOS,
              icono: ''
            },
            {
              nombre: 'Categorías',
              ruta: RoutesUrl.FIN_CONCEPTOS,
              icono: ''
            },
            {
              nombre: 'Cuentas',
              ruta: RoutesUrl.FIN_CUENTAS,
              icono: ''
            },
          ]
        },

      ]
    },

    {
      nombre: 'Finanzas Liga',
      ruta: '',
      icono: 'tt_invoice',
      tipo: ['LIGA'],
      roles: ['ROOT', 'ADMIN'],
      hijos: [
        {
          nombre: 'Ingresos y CPC',
          ruta: RoutesUrl.FINLI_INGRESOS,
          icono: ''
        },
        {
          nombre: 'Gastos',
          ruta: RoutesUrl.FINLI_EGRESOS,
          icono: ''
        },
        {
          nombre: 'Saldo de Equipos',
          ruta: RoutesUrl.FINLI_SALDOS_EQUIPOS,
          icono: ''
        },
        {
          nombre: 'Configuración',
          icono: '',
          nietos: [
            {
              nombre: 'Descuentos',
              ruta: RoutesUrl.FINLI_DESCUENTOS,
              icono: ''
            },
            {
              nombre: 'Categorías',
              ruta: RoutesUrl.FINLI_CONCEPTOS,
              icono: ''
            },
            {
              nombre: 'Cuentas',
              ruta: RoutesUrl.FINLI_CUENTAS,
              icono: ''
            },
            {
              nombre: 'Tipos de Multa',
              ruta: RoutesUrl.FINLI_MULTAS,
              icono: ''
            },
          ]
        },
      ]
    },

    {
      nombre: 'Académico',
      icono: 'tt_equipos',
      tipo: ['EQUIPO'],
      hijos: [
        {
          nombre: 'Ciclos y Grupos',
          ruta: RoutesUrl.CICLOS_Y_GRUPOS,
          icono: 'tt_group1',
          tipo: ['EQUIPO'],
          permiso: ['gestionar-grupos'],
        },
      ]
    },

    {
      nombre: 'Admin & Config',
      icono: 'tt_configuracion',
      // Sin roles/permiso propio: se muestra si al menos un hijo pasa su propio filtro.
      hijos: [
        {
          nombre: 'Usuarios',
          ruta: RoutesUrl.USUARIOS_ADMIN,
          icono: 'tt_equipos',
          permiso: ['gestionar-usuarios-escuela'],
        },
        {
          nombre: 'Roles',
          ruta: RoutesUrl.ROLES_ESCUELA,
          icono: 'tt_equipos',
          tipo: ['EQUIPO'],
          permiso: ['crear-roles-escuela'],
        },
        {
          nombre: 'Documentos',
          ruta: RoutesUrl.DOCS,
          icono: 'tt_am-ficha-jug',
          permiso: ['gestionar-catalogo-documentos'],
        },
        {
          nombre: 'Tags de Noticias',
          ruta: RoutesUrl.NOTICIA_TAGS_GLOBALES,
          icono: 'tt-americano-news',
          tipo: ['GLOBAL'],
          roles: ['ROOT'],
        },
        {
          nombre: 'Categorías y Divs',
          ruta: RoutesUrl.CATEGORIAS_DIVISIONES,
          icono: 'tt_trofeo',
          tipo: [], // Oculto: categorías/modalidades deportivas sin equivalente escolar.
          permiso: ['gestionar-categorias', 'gestionar-divisiones'],
        },
        {
          nombre: 'Niveles y Grados',
          ruta: RoutesUrl.NIVELES_GRADOS,
          icono: 'tt_graduado',
          tipo: ['EQUIPO'],
          permiso: ['gestionar-niveles-grados'],
        },
        {
          nombre: 'Árbitros',
          ruta: RoutesUrl.ARBITROS,
          icono: 'tt_silbato',
          tipo: ['LIGA'],
          permiso: ['gestionar-arbitros'],
        }
      ]
    },



    //{ nombre: 'Torneos', ruta: RoutesUrl.TORNEOS, iconoUrl: './assets/svg/amer-torneo' },
    //{ nombre: 'Equipos', ruta: RoutesUrl.EQUIPOS, iconoUrl: './assets/svg/equipos' },
    //{ nombre: 'Sedes', ruta: RoutesUrl.SEDES, iconoUrl: './assets/svg/estadio' },
    //{ nombre: 'Configuración', ruta: RoutesUrl.CONFIGURACION, iconoUrl: 'assets/svg/configuracion' },
  ];

  public logoAMostrar = computed(() => {
    const rol = this.rolActivoSignal();
    const logoGlobal = this.modoOscuro() ? 'assets/images/SC-Logo-White.png' : 'assets/images/SCLogo.png';
    // Prioridad: Escudo del equipo del rol activo -> Icono de la liga -> Logo global (según modo)
    return rol?.escudoURL || this.liga?.appIcon || logoGlobal;
  });
  toggleColapso() {
    this.menuColapsado.update(v => !v);
  }

  toggleModoOscuro() {
    this._theming.alternarModoOscuro();
    this.modoOscuro.set(this._theming.esModoOscuroActivo());
  }

  public opcionesMenuFiltradas = computed(() => {
    // Leemos las signals para que Angular sepa que dependemos de ellas
    this.rolActivoSignal();
    this.usuarioSignal();

    return this.filtrarOpciones(this.menuCompleto);
  });

  constructor() {
    // Lógica para cerrar el menú automáticamente al navegar
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Si navegamos, cerramos el menú (útil en móvil)
      this._servicioBarra.cerrarMenuLateral();
    });
  }

  /**
   * Filtra recursivamente las opciones basándose en Roles y Permisos
   */
  private filtrarOpciones(opciones: OpcionMenu[]): OpcionMenu[] {
    const rolActivo = this._sesion.getRolActivo();

    return opciones.reduce<OpcionMenu[]>((resultado, opcion) => {

      // 1. Filtrar por tipo: si el ítem declara tipo, el contexto activo debe
      // estar en esa lista (si se omite, es visible para todos).
      if (opcion.tipo && (!rolActivo || !opcion.tipo.includes(rolActivo.tipo))) {
        return resultado;
      }

      // 2. Validar ROL
      if (opcion.roles && !this._sesion.verificarPermisoRol(opcion.roles)) {
        return resultado;
      }

      // 2. Validar PERMISO (si es array, basta con tener cualquiera de ellos)
      if (opcion.permiso) {
        const permisos = Array.isArray(opcion.permiso) ? opcion.permiso : [opcion.permiso];
        if (!permisos.some(p => this._sesion.can(p))) return resultado;
      }

      // 2b. Exige que exista una CrmConfig para el contexto activo
      if (opcion.requiereConfigCrm && !this._crmConfigDisponible()) return resultado;

      let opcionFiltrada = opcion;

      // 3. Filtrar HIJOS de forma recursiva y reemplazarlos por la lista ya filtrada
      if (opcion.hijos && opcion.hijos.length > 0) {
        const hijosFiltrados = this.filtrarOpciones(opcion.hijos);
        if (hijosFiltrados.length === 0) return resultado;
        opcionFiltrada = { ...opcionFiltrada, hijos: hijosFiltrados };
      }

      // 4. Filtrar NIETOS de forma recursiva y reemplazarlos por la lista ya filtrada
      if (opcion.nietos && opcion.nietos.length > 0) {
        const nietosFiltrados = this.filtrarOpciones(opcion.nietos);
        if (nietosFiltrados.length === 0) return resultado;
        opcionFiltrada = { ...opcionFiltrada, nietos: nietosFiltrados };
      }

      resultado.push(opcionFiltrada);
      return resultado;
    }, []);
  }

  // Nombre del feed de noticias personalizable por liga (liga.config.nombreFeedNoticias)
  public obtenerNombreOpcion(opcion: OpcionMenu): string {
    if (opcion.ruta === RoutesUrl.NOTICIAS) {
      return this.liga?.config?.nombreFeedNoticias || opcion.nombre;
    }
    return opcion.nombre;
  }

  cerrarMenu() {
    this._servicioBarra.cerrarMenuLateral();
  }

  toggleMenu(opcion: OpcionMenu) {
    if (opcion.hijos || opcion.nietos) {
      opcion.desplegado = !opcion.desplegado;
    }
  }
}
